/**
 * O ÚNICO ponto de saída HTTP do front.
 *
 * Restrição inviolável: o front fala com a API .NET e com mais nada. Nunca
 * o sistema legado, nunca o Microsoft Graph, nunca um modelo de linguagem.
 * O lint barra os SDKs por import; este arquivo é o outro lado da regra —
 * `fetch` é proibido em qualquer outro módulo (`no-restricted-globals`).
 *
 * Aqui também mora o correlation_id: ele NASCE na fronteira do gatilho e é
 * carregado, nunca regenerado. Em retry vai o mesmo valor — se regenerar,
 * a idempotência do outro lado morre.
 */

import { ErroDaApi, type ProblemDetails } from './erros';

const BASE = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/+$/, '');

export const CABECALHO_DE_CORRELACAO = 'X-Correlation-Id';

/** O código que a API devolve quando a sessão venceu ou foi encerrada. */
export const CODIGO_DE_SESSAO_EXPIRADA = 'SESSION_EXPIRED';

const ouvintesDeSessaoExpirada = new Set<() => void>();

/**
 * Avisa quando QUALQUER chamada volta `401 SESSION_EXPIRED`.
 *
 * A sessão morre sozinha com 60 min sem uso (`ADR-0011`). Sem um aviso
 * central, cada tela trataria o 401 do seu jeito, e a pessoa ficaria olhando
 * uma fila com erro em vez de voltar ao login. Quem assina é a porta de
 * identidade, que zera a sessão; a guarda de rota faz o resto.
 */
export function aoExpirarSessao(ouvinte: () => void): () => void {
  ouvintesDeSessaoExpirada.add(ouvinte);
  return () => {
    ouvintesDeSessaoExpirada.delete(ouvinte);
  };
}

/** Gera um identificador de correlação novo — só na fronteira do gatilho. */
export function novoCorrelationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `corr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface OpcoesDeRequisicao {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /**
   * `FormData` passa DIRETO para o `fetch`, sem `JSON.stringify` e sem
   * `Content-Type` manual — é o browser que gera o boundary do
   * `multipart/form-data` (TP-049, T9: envio do PDF da fonte). Qualquer
   * outro valor continua virando corpo JSON, como sempre.
   */
  corpo?: unknown;
  sinal?: AbortSignal;
  /** Passe o ORIGINAL em reprocesso. Só omita quando o gatilho nasce aqui. */
  correlationId?: string;
}

function montarUrl(caminho: string): string {
  return caminho.startsWith('http') ? caminho : `${BASE}${caminho.startsWith('/') ? caminho : `/${caminho}`}`;
}

/**
 * A mesma URL que `requisitar` monta, exportada para os poucos lugares que
 * precisam de um `href` em vez de uma chamada em JavaScript — download de
 * anexo/e-mail original (`T13`), que a sessão por cookie resolve pela
 * navegação normal do browser, sem passar por `fetch`. Continua sendo UM
 * lugar só que sabe montar a URL da API; sem isto, cada tela reimplementaria
 * `BASE` por conta própria.
 */
export function montarUrlDaApi(caminho: string): string {
  return montarUrl(caminho);
}

async function lerProblemDetails(resposta: Response): Promise<ProblemDetails | null> {
  try {
    const texto = await resposta.text();
    if (texto.trim() === '') return null;
    return JSON.parse(texto) as ProblemDetails;
  } catch {
    return null;
  }
}

/**
 * O núcleo comum a `requisitar` e `requisitarTexto`: monta a requisição,
 * distingue erro de rede de cancelamento, e traduz `!ok` em `ErroDaApi` com
 * `ProblemDetails`. O que muda entre JSON e texto é só como o corpo do `200`
 * é lido — por isso só essa parte fica fora daqui.
 */
async function executar(
  caminho: string,
  opcoes: OpcoesDeRequisicao,
  aceitar: string,
): Promise<{ resposta: Response; correlationId: string }> {
  const correlationId = opcoes.correlationId ?? novoCorrelationId();
  const corpoEhFormData = opcoes.corpo instanceof FormData;
  const cabecalhos: Record<string, string> = {
    Accept: aceitar,
    [CABECALHO_DE_CORRELACAO]: correlationId,
  };
  if (opcoes.corpo !== undefined && !corpoEhFormData) cabecalhos['Content-Type'] = 'application/json';

  let resposta: Response;
  try {
    resposta = await fetch(montarUrl(caminho), {
      method: opcoes.method ?? 'GET',
      headers: cabecalhos,
      body:
        opcoes.corpo === undefined
          ? undefined
          : corpoEhFormData
            ? (opcoes.corpo as FormData)
            : JSON.stringify(opcoes.corpo),
      signal: opcoes.sinal ?? null,
      credentials: 'same-origin',
    });
  } catch (causa) {
    if (causa instanceof DOMException && causa.name === 'AbortError') {
      throw new ErroDaApi({ mensagem: 'Requisição cancelada.', origem: 'cancelado', correlationId });
    }
    // A API fora do ar é ESTE caminho. A tela precisa mostrar erro
    // explícito — apresentar isso como lista vazia seria mentir que não
    // há pendência.
    throw new ErroDaApi({
      mensagem: 'Não foi possível falar com a API da Anery. Verifique a conexão e tente de novo.',
      origem: 'rede',
      correlationId,
    });
  }

  if (!resposta.ok) {
    const problema = await lerProblemDetails(resposta);
    if (resposta.status === 401 && problema?.codigo === CODIGO_DE_SESSAO_EXPIRADA) {
      for (const ouvinte of ouvintesDeSessaoExpirada) ouvinte();
    }
    throw new ErroDaApi({
      mensagem: problema?.detail ?? problema?.title ?? `A API respondeu ${resposta.status}.`,
      origem: 'http',
      status: resposta.status,
      codigo: problema?.codigo ?? null,
      // O correlation_id da resposta VENCE o nosso: é o que o servidor
      // registrou, e é por ele que a operação acha a execução no log.
      correlationId: problema?.correlation_id ?? resposta.headers.get(CABECALHO_DE_CORRELACAO) ?? correlationId,
    });
  }

  return { resposta, correlationId };
}

export async function requisitar<T>(caminho: string, opcoes: OpcoesDeRequisicao = {}): Promise<T> {
  const { resposta, correlationId } = await executar(caminho, opcoes, 'application/json');

  if (resposta.status === 204) return undefined as T;

  try {
    return (await resposta.json()) as T;
  } catch {
    throw new ErroDaApi({
      mensagem: 'A API respondeu em um formato que o front não entende.',
      origem: 'formato',
      status: resposta.status,
      correlationId,
    });
  }
}

/**
 * Igual a `requisitar`, mas devolve o `status` HTTP junto do corpo.
 *
 * Único consumo hoje: `POST /contratos/{id}/fontes` (`TP-049`, T9, `D6`), em
 * que `200` (fonte já existia, pelo hash) e `201` (fonte nova) devolvem o
 * MESMO corpo (`{ id }`) — a diferença está só no status, e `requisitar`
 * joga essa informação fora. Sem isto, a tela não teria como distinguir
 * "reenvio aponta para a fonte existente" de "fonte nova criada".
 */
/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters --
   mesmo padrao de requisitar<T> acima: T so e inferivel pelo chamador, nao por nenhum parametro. */
export async function requisitarComStatus<T>(
  caminho: string,
  opcoes: OpcoesDeRequisicao = {},
): Promise<{ dado: T; status: number }> {
  const { resposta, correlationId } = await executar(caminho, opcoes, 'application/json');

  if (resposta.status === 204) return { dado: undefined as T, status: resposta.status };

  try {
    return { dado: (await resposta.json()) as T, status: resposta.status };
  } catch {
    throw new ErroDaApi({
      mensagem: 'A API respondeu em um formato que o front não entende.',
      origem: 'formato',
      status: resposta.status,
      correlationId,
    });
  }
}

/**
 * Igual a `requisitar`, mas para corpo que não é JSON — hoje só o `.eml`
 * cru do e-mail de origem da triagem (`T13`, `application/octet-stream`).
 * Mesmo tratamento de erro, mesmo correlation_id; o que muda é só `.text()`
 * em vez de `.json()`.
 */
export async function requisitarTexto(caminho: string, opcoes: OpcoesDeRequisicao = {}): Promise<string> {
  const { resposta } = await executar(caminho, opcoes, '*/*');
  if (resposta.status === 204) return '';
  return resposta.text();
}
